import { useEffect, useState } from "react";
import { ExternalLink, Mic, Send, Headphones } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import { HERO_NINA as HERO } from "@/lib/assets";

export default function Radio() {
  const [episodes, setEpisodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    artist_name: "",
    email: "",
    track_title: "",
    genre: "",
    streaming_link: "",
    instagram: "",
    bio: "",
  });

  useEffect(() => {
    api
      .get("/public/podcasts")
      .then((r) => setEpisodes(r.data.episodes || []))
      .catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/music-submission", form);
      toast.success("Submission received. Nina's team will be in touch.");
      setForm({
        artist_name: "",
        email: "",
        track_title: "",
        genre: "",
        streaming_link: "",
        instagram: "",
        bio: "",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="radio-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden">
        <img
          src={HERO}
          alt="In Da Streets Radio — Nina Capone"
          className="absolute inset-0 w-full h-full object-cover object-[70%_30%] sm:object-[60%_35%] lg:object-[center_38%]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.15) 0%, rgba(5,3,8,0.05) 30%, rgba(5,3,8,0.65) 70%, #050308 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(5,3,8,0.85) 55%, #050308 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-12 pt-24 w-full">
          <div className="max-w-3xl space-y-6 fade-up">
            <h1 className="font-serif font-light tracking-tighter text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.95]">
              In Da Streets
              <br />
              <em
                className="italic font-light"
                style={{ color: "var(--gold)" }}
              >
                Radio.
              </em>
            </h1>
            <p className="text-white/80 text-base sm:text-lg max-w-xl leading-relaxed">
              Twenty-four hours. Seven days. Global signal. The independent
              station built by an artist for the artists — Philly soul,
              international polish.
            </p>
            <div
              className="inline-flex items-center gap-3 px-4 py-2 border"
              style={{
                borderColor: "rgba(232,180,184,0.45)",
                background: "rgba(5,3,8,0.55)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--gold)" }}
              />
              <p className="eyebrow !text-[0.7rem]">
                01 · The Empire
              </p>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="https://open.spotify.com/album/2XDRxKesvCeCc6oS4dfX3g?si=O4VMDHl0T_K3rdG3-oHpfA"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-3 px-7 py-3 text-xs"
                data-testid="radio-listen-live-button"
              >
                <span className="inline-block w-2 h-2 bg-black rounded-full live-dot" />
                Listen Live · Spotify Exclusive
                <ExternalLink size={14} />
              </a>
              <a
                href="https://indastreetsradio.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                data-testid="radio-station-link"
              >
                Visit indastreetsradio.com
                <ExternalLink size={14} />
              </a>
              <a
                href="#submit"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                data-testid="radio-submit-music-cta"
              >
                Submit Your Music
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PODCAST */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-7">
              <SectionLabel number="02" label="Podcasts" />
              <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light">
                Respect The Hustle
                <br />
                <em className="italic" style={{ color: "var(--gold)" }}>
                  & 10 Minutes Of Talk.
                </em>
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-white/60 leading-relaxed">
                Two shows, one philosophy. <em className="italic">Respect The Hustle</em> runs long-form
                with industry creatives. <em className="italic">10 Minutes Of Talk</em> hits short,
                sharp, and usable. Stream both on YouTube.
              </p>
            </div>
          </div>

          {["Respect The Hustle", "10 Minutes Of Talk"].map((show) => {
            const showEps = episodes.filter((e) => e.show === show);
            if (showEps.length === 0) return null;
            return (
              <div key={show} className="mb-16" data-testid={`podcast-show-${show.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-end justify-between mb-6 pb-4 border-b border-white/10">
                  <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
                    {show}
                  </h3>
                  <a
                    href={showEps[0].external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs uppercase tracking-[0.25em]"
                    style={{ color: "var(--gold)" }}
                  >
                    Full Playlist →
                  </a>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {showEps.map((ep) => (
                    <article
                      key={ep.id}
                      className="surface p-8 lg:p-10 group"
                      data-testid={`podcast-episode-${ep.episode_number}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-6">
                        <span
                          className="font-serif text-5xl tracking-tighter"
                          style={{ color: "var(--gold)" }}
                        >
                          {String(ep.episode_number).padStart(2, "0")}
                        </span>
                        <span className="eyebrow">{ep.duration}</span>
                      </div>
                      <h4 className="font-serif text-2xl lg:text-3xl tracking-tighter mb-3 leading-tight">
                        {ep.title}
                      </h4>
                      <p className="text-sm text-white/50 mb-2">
                        with{" "}
                        <span className="text-white/80 italic font-serif">
                          {ep.guest}
                        </span>
                      </p>
                      <p className="text-sm text-white/60 leading-relaxed mb-6">
                        {ep.description}
                      </p>
                      <a
                        href={ep.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                        style={{ color: "var(--gold)" }}
                      >
                        <Headphones size={14} /> Stream On YouTube
                      </a>
                      {(() => {
                        const m = (ep.external_url || "").match(/[?&]v=([^&]+)/);
                        const vid = m ? m[1] : null;
                        if (!vid) return null;
                        const origin =
                          typeof window !== "undefined"
                            ? window.location.origin
                            : "";
                        return (
                          <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-3">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                              Share
                            </span>
                            <ShareButtons
                              shareUrl={`${origin}/api/og/podcast/${vid}`}
                              canonicalUrl={`${origin}/radio`}
                              text={`${ep.title} — ${ep.show} · Nina Capone`}
                              testidPrefix={`pod-share-${ep.episode_number}`}
                              size="sm"
                            />
                          </div>
                        );
                      })()}
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SUBMIT MUSIC */}
      <section
        id="submit"
        className="py-28 border-t border-white/5"
        data-testid="submit-music-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <SectionLabel number="03" label="Submission" />
            <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light leading-[1]">
              Submit your <em className="italic" style={{ color: "var(--gold)" }}>music</em>.
            </h2>
            <p className="text-white/60 leading-relaxed max-w-md">
              Independent artists with a story and a sound get rotation on In
              Da Streets Radio. We listen to everything submitted. No payola.
              No politics. Just the work.
            </p>
            <ul className="space-y-3 text-sm text-white/60 pt-4">
              <li className="flex gap-3">
                <Mic size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                Originals only — no covers or unauthorized samples.
              </li>
              <li className="flex gap-3">
                <Mic size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                Streaming link to a final-mastered track (Spotify / Apple / SoundCloud).
              </li>
              <li className="flex gap-3">
                <Mic size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                Response window: 14 days for selected artists.
              </li>
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 space-y-6 surface p-8 lg:p-10"
            data-testid="music-submission-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Artist Name" id="artist_name">
                <Input
                  id="artist_name"
                  required
                  value={form.artist_name}
                  onChange={(e) =>
                    setForm({ ...form, artist_name: e.target.value })
                  }
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-artist-name"
                />
              </Field>
              <Field label="Email" id="email">
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-email"
                />
              </Field>
              <Field label="Track Title" id="track_title">
                <Input
                  id="track_title"
                  required
                  value={form.track_title}
                  onChange={(e) =>
                    setForm({ ...form, track_title: e.target.value })
                  }
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-track-title"
                />
              </Field>
              <Field label="Genre" id="genre">
                <Input
                  id="genre"
                  required
                  placeholder="Hip Hop / R&B / Soul / Faith…"
                  value={form.genre}
                  onChange={(e) => setForm({ ...form, genre: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-genre"
                />
              </Field>
              <Field label="Streaming Link" id="streaming_link" full>
                <Input
                  id="streaming_link"
                  required
                  placeholder="https://"
                  value={form.streaming_link}
                  onChange={(e) =>
                    setForm({ ...form, streaming_link: e.target.value })
                  }
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-streaming-link"
                />
              </Field>
              <Field label="Instagram (optional)" id="instagram" full>
                <Input
                  id="instagram"
                  value={form.instagram}
                  onChange={(e) =>
                    setForm({ ...form, instagram: e.target.value })
                  }
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="submission-instagram"
                />
              </Field>
            </div>
            <Field label="Short Bio" id="bio">
              <Textarea
                id="bio"
                rows={4}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="bg-transparent border border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="submission-bio"
              />
            </Field>
            <Button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full sm:w-auto px-10 py-6 text-xs rounded-none"
              data-testid="submission-submit-button"
            >
              <Send size={14} />
              {submitting ? "Submitting…" : "Submit Your Track"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, full, children }) {
  return (
    <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="eyebrow">
        {label}
      </Label>
      {children}
    </div>
  );
}
